import React from 'react'
import { cn } from '@/utils'
import Loader from './Loader'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'link'
  size?: 'sm' | 'md' | 'lg'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    className,
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    children,
    disabled,
    ...props
  }, ref) => {
    const variants = {
      primary: 'bg-foreground text-background hover:bg-[#C8FF00] hover:text-black focus:ring-[#C8FF00] transition-colors duration-200 rounded-lg font-medium shadow-md',
      secondary: 'bg-secondary text-secondary-foreground hover:bg-[#C8FF00] hover:text-black focus:ring-[#C8FF00] transition-colors duration-200 rounded-lg font-medium',
      outline: 'border border-border text-foreground hover:bg-[#C8FF00] hover:text-black hover:border-[#C8FF00] focus:ring-[#C8FF00] transition-colors duration-200 rounded-lg font-medium',
      ghost: 'text-foreground hover:bg-[#C8FF00] hover:text-black focus:ring-[#C8FF00] transition-colors duration-200 rounded-lg font-medium',
      link: 'text-foreground underline-offset-4 hover:text-[#C8FF00] hover:underline focus:ring-[#C8FF00]'
    }

    const sizes = {
      sm: 'h-8 px-3 text-sm',
      md: 'h-10 px-4',
      lg: 'h-12 px-6 text-lg'
    }

    return (
      <button
        ref={ref}
        className={cn(
          'relative inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          variants[variant],
          sizes[size],
          className
        )}
        disabled={disabled || isLoading}
        {...props}
      >
        {isLoading && (
          <Loader className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        )}
        <span className={cn('flex items-center gap-2', isLoading && 'invisible')}>
          {leftIcon}
          {children}
          {rightIcon}
        </span>
      </button>
    )
  }
)
Button.displayName = 'Button'

export default Button 